/**
 * Site-level BTM verdict resolution for portfolio / overview map pins.
 *
 * Prefers the latest usable saved executive-summary report on the site, then
 * falls back to the most recent usable saved report of any type.
 */
import type { SiteEntity } from '../types/site';
import type { SiteRagReport } from '../types/siteRag';
import type { ReportTypeId } from '../config/reportTypes';
import { getDisplayVerdict, type VerdictTier } from './reportVerdict';
import { latestUsableSavedReportOfType } from './pathToGo/reportPlan';
import { latestExecutiveSummaryForDecision } from './savedReportDecisionSource';

/** Scored report types consulted when exec summary has no display tier. */
const BTM_VERDICT_TIER_FALLBACK: ReportTypeId[] = [
  'feasibility',
  'power-availability',
  'environmental',
  'test-fit',
];

export function siteReportsForVerdict(
  site: Pick<SiteEntity, 'id' | 'name' | 'reportIds'>,
  allReports: ReadonlyArray<SiteRagReport>,
): SiteRagReport[] {
  const reportIds = new Set(site.reportIds ?? []);
  return allReports
    .filter(r =>
      r.siteId === site.id
      || (!r.siteId && reportIds.has(r.id)),
    )
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

function latestUsableReportByTypeFromList(reports: SiteRagReport[]): Map<ReportTypeId, SiteRagReport> {
  const out = new Map<ReportTypeId, SiteRagReport>();
  const types = new Set<ReportTypeId>();
  for (const r of reports) {
    types.add((r.reportType ?? 'feasibility') as ReportTypeId);
  }
  for (const type of types) {
    // Saved calculation fallback remains available for other types. A newer
    // quarantined summary must not revive an older narrative decision.
    const latestSummary = type === 'executive-summary' ? latestExecutiveSummaryForDecision(reports) : undefined;
    const report = latestUsableSavedReportOfType(
      type === 'executive-summary' ? (latestSummary ? [latestSummary] : []) : reports, type,
    );
    if (report) out.set(type, report);
  }
  return out;
}

/** Latest successfully saved calculation input for a site, across report types. */
export function latestUsableSavedReportForSite(
  site: Pick<SiteEntity, 'id' | 'name' | 'reportIds'>,
  allReports: ReadonlyArray<SiteRagReport>,
): SiteRagReport | null {
  const byType = latestUsableReportByTypeFromList(siteReportsForVerdict(site, allReports));
  return [...byType.values()].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  )[0] ?? null;
}

/** Report whose verdict drives the site map pin (exec summary wins). */
export function siteBtmVerdictReport(
  site: Pick<SiteEntity, 'id' | 'name' | 'reportIds'>,
  allReports: ReadonlyArray<SiteRagReport>,
): SiteRagReport | null {
  const siteReports = siteReportsForVerdict(site, allReports);
  if (siteReports.length === 0) return null;
  const byType = latestUsableReportByTypeFromList(siteReports);
  return byType.get('executive-summary') ?? [...byType.values()].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  )[0] ?? null;
}

export function siteBtmVerdictTier(
  site: Pick<SiteEntity, 'id' | 'name' | 'reportIds'>,
  allReports: ReadonlyArray<SiteRagReport>,
): VerdictTier {
  const siteReports = siteReportsForVerdict(site, allReports);
  if (siteReports.length === 0) return 'none';
  const byType = latestUsableReportByTypeFromList(siteReports);

  const exec = byType.get('executive-summary');
  if (exec) {
    const tier = getDisplayVerdict(exec).tier;
    if (tier !== 'none') return tier;
  }

  for (const typeId of BTM_VERDICT_TIER_FALLBACK) {
    const report = byType.get(typeId);
    if (!report) continue;
    const tier = getDisplayVerdict(report).tier;
    if (tier !== 'none') return tier;
  }

  const usableReports = [...byType.values()].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );
  for (const report of usableReports) {
    const tier = getDisplayVerdict(report).tier;
    if (tier !== 'none') return tier;
  }
  return 'none';
}

export function siteMapPosition(
  site: Pick<SiteEntity, 'location' | 'siteCentroid'>,
): { lat: number; lng: number } | null {
  const loc = site.location;
  if (loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lng) && !(loc.lat === 0 && loc.lng === 0)) {
    return loc;
  }
  const centroid = site.siteCentroid;
  if (centroid && Number.isFinite(centroid.lat) && Number.isFinite(centroid.lng)) {
    return centroid;
  }
  return null;
}

export interface PortfolioMapSitePin {
  siteId: string;
  siteName: string;
  position: { lat: number; lng: number };
  tier: VerdictTier;
}

export function buildPortfolioMapPins(
  sites: ReadonlyArray<SiteEntity>,
  allReports: ReadonlyArray<SiteRagReport>,
): PortfolioMapSitePin[] {
  const pins: PortfolioMapSitePin[] = [];
  for (const site of sites) {
    const position = siteMapPosition(site);
    if (!position) continue;
    pins.push({
      siteId: site.id,
      siteName: site.name,
      position,
      tier: siteBtmVerdictTier(site, allReports),
    });
  }
  return pins;
}
